'use strict';

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

const $ = jQuery.noConflict();

$(function() {

	const cards = {

		// How long each card rests (HOLD) and how long the crossfade between
		// two cards runs (TRANS) — in timeline time units, not seconds. The
		// ScrollTrigger maps scroll distance onto the whole timeline, so these
		// only set the *ratio* of "parked on a card" vs "transitioning".
		HOLD: 0.6,
		TRANS: 1,

		init: function() {
			this.cacheDOM();
			if (!this.$component.length) {
				return;
			}
			gsap.registerPlugin(ScrollTrigger);
			this.initStates();
			this.bindEvents();
			this.build();
		},

		cacheDOM: function() {
			this.$component = $('.js-cards');
			this.bgs = this.$component.find('.js-cards-bg-image').toArray();
			this.items = this.$component.find('.js-cards-item').toArray();
			this.fills = this.$component.find('.js-cards-progress-fill').toArray();
			this.activeIndex = 0;
			this.total = this.items.length;
			this.totalSteps = this.total - 1;
			this.lastWidth = window.innerWidth;
		},

		// One viewport-relative figure for how far you scroll per card. Kept
		// short so a card never dwells "scrolling in place" for long.
		stepDistance: function() {
			return Math.max(window.innerHeight, 500);
		},

		// Everything hidden up front except the first card/background, which are
		// visible from the start. Keeping card 0's base opacity at 1 (rather than
		// fading it in) is what makes scrubbing back to the top safe — the first
		// card never ends up invisible after a round trip. Inline opacity from
		// gsap.set wins over the .is-active CSS rule, so the class is only ever
		// used for pointer-events from here on.
		initStates: function() {
			gsap.set([...this.items, ...this.bgs], {opacity: 0});
			gsap.set(this.bgs, {scale: 1});
			gsap.set([this.items[0], this.bgs[0]], {opacity: 1});
			gsap.set(this.fills, {scaleY: 0, transformOrigin: 'top'});
		},

		bindEvents: function() {
			$(window).on('resize.cards', this.handleResize.bind(this));
		},

		// Only refresh on a real width change — mobile address-bar show/hide
		// fires resize on every scroll and would thrash the pin otherwise.
		handleResize: function() {
			const currentWidth = window.innerWidth;
			if (currentWidth === this.lastWidth) {
				return;
			}
			this.lastWidth = currentWidth;
			if (this.resizeTimer) {
				clearTimeout(this.resizeTimer);
			}
			this.resizeTimer = setTimeout(() => {
				ScrollTrigger.refresh();
			}, 200);
		},

		// Single card: it's already visible from initStates, just reveal its copy
		// and fill its progress dash once.
		buildSingle: function() {
			this.items[0].classList.add('is-active');
			if (this.fills[0]) {
				gsap.set(this.fills[0], {scaleY: 1});
			}
			const body = this.items[0].querySelector('.cards__body');
			if (body) {
				gsap.from(body, {opacity: 0, duration: 1, ease: 'power2.out'});
			}
		},

		build: function() {
			if (this.total < 2) {
				this.buildSingle();
				return;
			}

			// Card 0 is shown from the start (initStates keeps it opaque with its
			// copy in place — the white card would look empty otherwise), so its
			// progress dash starts full.
			if (this.fills[0]) {
				gsap.set(this.fills[0], {scaleY: 1});
			}
			this.items[0].classList.add('is-active');
			this.bgs[0].classList.add('is-active');

			const tl = gsap.timeline({
				defaults: {ease: 'none'},
				scrollTrigger: {
					trigger: this.$component[0],
					start: 'top top',
					end: () => `+=${this.totalSteps * this.stepDistance()}`,
					pin: true,
					pinSpacing: true,
					anticipatePin: 1,
					// scrub lerps the timeline toward the scroll position, so a
					// fast fling/swipe still plays through every card — the section
					// never "flies over" and skips the cards in between.
					scrub: 1,
					invalidateOnRefresh: true,
					onUpdate: this.updateActive.bind(this),
				},
			});

			const {HOLD, TRANS} = this;

			// Card 0's background gets the same slow drift as every other card,
			// just from the very start while it rests.
			if (this.bgs[0]) {
				tl.fromTo(this.bgs[0], {scale: 1}, {scale: 1.08, duration: TRANS + HOLD, ease: 'none'}, 0);
			}

			let t = HOLD; // initial rest on the first card

			// Each transition is identical: the incoming card/background dissolves
			// over the opaque one beneath (no wash-out, reverses as a fade), its
			// background drifts, its copy slides up, and its progress dash fills.
			for (let i = 0; i < this.totalSteps; i++) {
				const to = i + 1;
				const bg = this.bgs[to];
				const item = this.items[to];
				const fill = this.fills[to];

				tl.to(bg, {opacity: 1, duration: TRANS * 0.15, ease: 'power1.out'}, t);
				tl.to(item, {opacity: 1, duration: TRANS * 0.25, ease: 'power1.out'}, t);

				// Fade the outgoing card out once the incoming has covered it, so at
				// rest only the active card is visible. Otherwise a shorter active
				// card lets a taller one beneath (incl. the first card, which sets the
				// stage height) peek out below with its content showing. The incoming
				// card covers the overlap instantly, so only the non-overlapping strip
				// fades — no two stacked text blocks.
				tl.to(this.items[i], {opacity: 0, duration: TRANS * 0.15, ease: 'power1.in'}, t);

				// Slow zoom on the incoming background, across the hold too so it
				// keeps drifting gently while parked.
				tl.fromTo(bg, {scale: 1}, {scale: 1.08, duration: TRANS + HOLD, ease: 'none'}, t);

				// Progress dash fills as the card arrives; earlier dashes stay full,
				// later ones empty (and it all reverses cleanly on scroll-up).
				if (fill) {
					tl.fromTo(fill, {scaleY: 0}, {scaleY: 1, duration: TRANS, ease: 'none'}, t);
				}

				t += TRANS + HOLD;
			}
		},

		// Toggle .is-active purely for pointer-events (so only the card on
		// screen is clickable). Opacity is owned by the timeline.
		updateActive: function(self) {
			const index = Math.min(this.totalSteps, Math.round(self.progress * this.totalSteps));
			if (index === this.activeIndex) {
				return;
			}
			this.items[this.activeIndex].classList.remove('is-active');
			this.bgs[this.activeIndex].classList.remove('is-active');
			this.items[index].classList.add('is-active');
			this.bgs[index].classList.add('is-active');
			this.activeIndex = index;
		},
	};

	cards.init();
});
